import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme, useAuth, useData } from '../context';
import i18n from '../i18n';
import AuthModal from './AuthModal';

const LANGS = ['lt', 'en', 'pl', 'ru', 'ja', 'es', 'fr', 'et', 'lv', 'de'];
const F = 'Cinzel,Georgia,serif';

const NEWS_CATEGORIES = [
  {
    id: 'fantasy', label: 'FANTASY', icon: '🐉',
    sub: [
      'D&D','5E','PATHFINDER','SHADOWDARK','DAGGERHEART',
      'TALES OF THE VALIANT','DRAW STEEL','WARHAMMER FANTASY ROLEPLAY',
      'THE ONE RING RPG','BLADES IN THE DARK','MÖRK BORG','PIRATE BORG',
      'NUMENERA','OLD-SCHOOL ESSENTIALS','PENDRAGON','DRAGONBANE',
      'RUNEQUEST','DUNGEON CRAWL CLASSICS','COSMERE','SHADOW OF THE WEIRD WIZARD',
    ],
  },
  {
    id: 'sci-fi', label: 'SCI-FI', icon: '🚀',
    sub: [
      'STARFINDER','CYBERPUNK RED','TRAVELLER','SHADOWRUN',
      'STAR TREK ADVENTURES','LANCER','STAR WARS RPG',
      'TWILIGHT 2000','FALLOUT','CORIOLIS: THE GREAT DARK',
    ],
  },
  {
    id: 'horror', label: 'HORROR', icon: '💀',
    sub: [
      'CALL OF CTHULHU','WORLD OF DARKNESS','ALIEN RPG',
      'VAESEN','DEADLANDS','OLD GODS OF APPALACHIA','MOTHERSHIP',
    ],
  },
  {
    id: 'superhero', label: 'SUPERHERO', icon: '⚡',
    sub: ['MARVEL MULTIVERSE','MUTANTS & MASTERMINDS','CHAMPIONS'],
  },
];

const EVENTS_SUBMENU = [
  { icon: '🏆', key: 'contests', path: '/konkursai' },
  { icon: '🗳️', key: 'polls',    path: '/balsavimai' },
  { icon: '🗺️', key: 'travel',   path: '/planuokle' },
];

function NavItem({ label, active, onClick, children }) {
  return (
    <div className="nav-btn-wrap">
      <button className={`nav-btn${active ? ' active' : ''}`} onClick={onClick}>
        {label}{children}
      </button>
      {active && <div className="nav-btn-underline" />}
    </div>
  );
}

export default function Topbar() {
  const { t } = useTranslation();
  const { dark, setDark } = useTheme();
  const { user, logout, isAdmin } = useAuth();
  const { news } = useData();
  const navigate = useNavigate();
  const location = useLocation();

  const [showAuth,    setShowAuth]    = useState(false);
  const [authMode,    setAuthMode]    = useState('login');
  const [userDDOpen,  setUserDDOpen]  = useState(false);
  const userDDRef = useRef(null);
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [langOpen,    setLangOpen]    = useState(false);
  const [newsOpen,    setNewsOpen]    = useState(false);
  const [eventsOpen,  setEventsOpen]  = useState(false);
  const [hoveredCat,  setHoveredCat]  = useState('fantasy');

  const langRef     = useRef(null);
  const newsTimer   = useRef(null);
  const eventsTimer = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
      if (userDDRef.current && !userDDRef.current.contains(e.target)) setUserDDOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const changeLang = (l) => { i18n.changeLanguage(l); localStorage.setItem('fantastai_lang', l); };
  const openNews   = () => { clearTimeout(newsTimer.current); setNewsOpen(true); };
  const closeNews  = () => { newsTimer.current = setTimeout(() => setNewsOpen(false), 250); };
  const openEvents  = () => { clearTimeout(eventsTimer.current); setEventsOpen(true); };
  const closeEvents = () => { eventsTimer.current = setTimeout(() => setEventsOpen(false), 250); };
  const goTo = (path) => { navigate(path); setMenuOpen(false); setNewsOpen(false); setEventsOpen(false); };

  const featuredNews = news[0] || null;
  const activeCat    = NEWS_CATEGORIES.find(c => c.id === hoveredCat);

  const navLinks = [
    { path: '/clubs',     label: t('nav.clubs')     },
    { path: '/events',    label: t('nav.events')    },
    { path: '/larp',      label: t('nav.larp')      },
    { path: '/calendar',  label: t('nav.calendar')  },
    { path: '/locations', label: t('nav.locations') },
    { path: '/skelbimai', label: t('nav.listings')  },
  ];

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const Divider = () => (
    <div style={{
      width: 1, height: 26, flexShrink: 0,
      background: 'linear-gradient(180deg,transparent,rgba(240,192,64,0.3),transparent)',
      margin: '0 10px',
    }} />
  );

  return (
    <>
      <header style={{
        background: dark
          ? 'linear-gradient(180deg,#04020c 0%,#0a0618 100%)'
          : 'linear-gradient(180deg,#1a1050 0%,#231466 100%)',
        position: 'sticky', top: 0, zIndex: 200,
        boxShadow: dark ? '0 4px 40px rgba(0,0,0,0.7)' : '0 4px 24px rgba(26,16,80,0.35)',
        transition: 'background 0.3s, box-shadow 0.3s',
      }}>
        {/* ── Top ornament ── */}
        <div style={{ height: 3, background: 'linear-gradient(90deg,transparent,#f0c040 22%,#7b4fd4 50%,#f0c040 78%,transparent)' }} />

        <div style={{ maxWidth: 1440, margin: '0 auto', display: 'flex', alignItems: 'center', height: 58, padding: '0 20px' }}>

          {/* Logo */}
          <div
            onClick={() => navigate('/')}
            style={{
              fontFamily: F, fontSize: 15, fontWeight: 900, letterSpacing: 3,
              color: '#f0c040', cursor: 'pointer', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 9,
              textShadow: '0 0 28px rgba(240,192,64,0.55)',
              marginRight: 4,
            }}
          >
            <span style={{ fontSize: 19, filter: 'drop-shadow(0 0 8px rgba(240,192,64,0.5))' }}>⚔️</span>
            FANTASTAI.LT
          </div>

          <Divider />

          {/* Desktop nav */}
          <nav className="topbar-nav" style={{ display: 'flex', alignItems: 'center', flex: 1, height: '100%' }}>

            {/* Regular links (events gets a submenu dropdown) */}
            {navLinks.map(({ path, label }) => (
              path === '/events' ? (
                <div
                  key={path}
                  style={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                  onMouseEnter={openEvents}
                  onMouseLeave={closeEvents}
                >
                  <NavItem label={label} active={isActive(path)} onClick={() => navigate(path)}>
                    <span style={{ fontSize: 8, marginLeft: 3, opacity: 0.5 }}>▾</span>
                  </NavItem>

                  {eventsOpen && (
                    <div className="events-dropdown" onMouseEnter={openEvents} onMouseLeave={closeEvents}>
                      <div className="events-dd-grid">
                        {EVENTS_SUBMENU.map(item => (
                          <button key={item.path} className="events-dd-card" onClick={() => goTo(item.path)}>
                            <span className="events-dd-icon">{item.icon}</span>
                            <span className="events-dd-title">{t(`events_menu.${item.key}_title`)}</span>
                            <span className="events-dd-desc">{t(`events_menu.${item.key}_desc`)}</span>
                            <span className="events-dd-open">{t('events_menu.open')} →</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavItem key={path} label={label} active={isActive(path)} onClick={() => navigate(path)} />
              )
            ))}

            {/* ── News mega-dropdown ── */}
            <div
              style={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center', flexShrink: 0 }}
              onMouseEnter={openNews}
              onMouseLeave={closeNews}
            >
              <NavItem
                label={t('nav.news')}
                active={location.pathname.startsWith('/news')}
                onClick={() => navigate('/news')}
              >
                <span style={{ fontSize: 8, marginLeft: 3, opacity: 0.5 }}>▾</span>
              </NavItem>

              {newsOpen && (
                <div className="news-dropdown" onMouseEnter={openNews} onMouseLeave={closeNews}>
                  <div className="news-dropdown-inner">

                    {/* Left: category tiles + subcats + footer */}
                    <div className="news-cats-col">

                      {/* 1×4 horizontal tiles */}
                      <div className="news-cat-grid">
                        {NEWS_CATEGORIES.map(cat => (
                          <button
                            key={cat.id}
                            className={`news-cat-btn${hoveredCat === cat.id ? ' sel' : ''}`}
                            onMouseEnter={() => setHoveredCat(cat.id)}
                            onClick={() => goTo(`/news?cat=${cat.id}`)}
                          >
                            <span className="news-cat-icon">{cat.icon}</span>
                            {cat.label}
                          </button>
                        ))}
                      </div>

                      {/* Subcategory chip strip */}
                      {activeCat?.sub.length > 0 && (
                        <div className="news-subs">
                          {activeCat.sub.map(s => (
                            <button
                              key={s}
                              className="news-sub-chip"
                              onClick={() => goTo(`/news?cat=${hoveredCat}&sub=${encodeURIComponent(s)}`)}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* "all news" footer */}
                      <div className="news-footer-row">
                        <button className="news-all-btn" onClick={() => goTo('/news')} style={{ textTransform: 'uppercase' }}>
                          {t('common.all_news')}
                        </button>
                      </div>
                    </div>

                    {/* Right: featured news card */}
                    <div className="news-featured-col">
                      <div className="news-featured-label" style={{ textTransform: 'uppercase' }}>{t('news.latest')}</div>
                      {featuredNews ? (
                        <div className="news-featured-card" onClick={() => goTo(`/news/${featuredNews.id}`)}>
                          <div
                            className="news-featured-thumb"
                            style={featuredNews.image_url ? { backgroundImage: `url(${featuredNews.image_url})` } : undefined}
                          >
                            {!featuredNews.image_url && '📰'}
                          </div>
                          {featuredNews.category && (
                            <span className="news-featured-cat">{featuredNews.category}</span>
                          )}
                          <div className="news-featured-title">{featuredNews.title}</div>
                          <div className="news-featured-date">
                            {(featuredNews.created_at || featuredNews.date || '').slice(0, 10)}
                          </div>
                        </div>
                      ) : (
                        <div className="news-featured-empty">{t('common.loading')}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <NavItem label={t('nav.publishers')} active={isActive('/publishers')} onClick={() => navigate('/publishers')} />
            <NavItem label={t('nav.players')} active={isActive('/players')} onClick={() => navigate('/players')} />

            {isAdmin && (
              <NavItem label={t('nav.admin')} active={isActive('/admin')} onClick={() => navigate('/admin')} />
            )}
          </nav>

          <Divider />

          {/* Right controls */}
          <div className="topbar-right" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>

            {/* Language */}
            <div ref={langRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setLangOpen(o => !o)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: langOpen ? 'rgba(240,192,64,0.12)' : 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(240,192,64,0.22)',
                  borderRadius: 6, color: '#f0c040', padding: '5px 10px',
                  cursor: 'pointer', fontFamily: F, fontSize: 10, fontWeight: 700, letterSpacing: 1,
                  transition: 'all .15s',
                }}
              >
                🌐 {i18n.language.toUpperCase()} <span style={{ fontSize: 9, opacity: 0.52 }}>▾</span>
              </button>
              {langOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                  background: dark ? 'linear-gradient(180deg,#09061c,#060412)' : '#fff',
                  border: dark ? '1px solid rgba(240,192,64,0.2)' : '1px solid rgba(91,88,214,0.25)',
                  borderRadius: 8, padding: '8px', zIndex: 300,
                  display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 4, minWidth: 162,
                  boxShadow: dark ? '0 8px 32px rgba(0,0,0,0.8)' : '0 8px 32px rgba(15,32,63,0.18)',
                }}>
                  {LANGS.map(l => (
                    <button key={l} onClick={() => { changeLang(l); setLangOpen(false); }} style={{
                      background: i18n.language === l ? 'rgba(240,192,64,0.12)' : 'none',
                      border: `1px solid ${i18n.language === l ? 'rgba(240,192,64,0.4)' : 'transparent'}`,
                      borderRadius: 4,
                      color: i18n.language === l ? '#f0c040' : (dark ? 'rgba(210,195,250,0.6)' : '#354161'),
                      padding: '5px 4px', cursor: 'pointer',
                      fontFamily: F, fontSize: 10, fontWeight: 700, letterSpacing: 1, textAlign: 'center',
                      transition: 'all .12s',
                    }}>
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Theme */}
            <button
              onClick={() => setDark(d => !d)}
              title="Toggle theme"
              style={{
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 6, padding: '5px 9px', cursor: 'pointer', fontSize: 14,
                transition: 'all .15s',
              }}
            >
              {dark ? '☀️' : '🌙'}
            </button>

            {/* Auth */}
            {user ? (
              <div ref={userDDRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setUserDDOpen(o => !o)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    background: userDDOpen ? 'rgba(240,192,64,0.12)' : 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(240,192,64,0.28)',
                    borderRadius: 8, padding: '5px 10px', cursor: 'pointer', transition: 'all .15s',
                  }}
                >
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: 'linear-gradient(135deg,#5a3e8c,#7b4fd4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: F, fontSize: 11, fontWeight: 700, color: '#f0c040', flexShrink: 0,
                  }}>
                    {(user.nickname || user.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <span style={{ color: '#f0c040', fontSize: 11, fontFamily: F, letterSpacing: 0.5, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.nickname || user.name}
                  </span>
                  <span style={{ fontSize: 8, opacity: 0.5, color: '#f0c040' }}>▾</span>
                </button>
                {userDDOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: 170,
                    background: 'linear-gradient(180deg,#0c0820,#07051a)',
                    border: '1px solid rgba(240,192,64,0.18)', borderRadius: 10,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.7)', overflow: 'hidden', zIndex: 300,
                  }}>
                    <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: 11, color: 'rgba(210,195,250,0.5)', fontFamily: F }}>
                      {user.email}
                    </div>
                    {[
                      { icon: '👤', label: t('nav.profile'), action: () => { navigate('/profile'); setUserDDOpen(false); } },
                      { icon: '🎲', label: t('nav.players'), action: () => { navigate('/players'); setUserDDOpen(false); } },
                    ].map(item => (
                      <button key={item.label} onClick={item.action} style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                        background: 'none', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)',
                        color: 'rgba(210,195,250,0.8)', padding: '10px 14px', cursor: 'pointer',
                        fontFamily: F, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textAlign: 'left',
                        transition: 'background .12s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(240,192,64,0.07)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        {item.icon} {item.label}
                      </button>
                    ))}
                    <button onClick={() => { logout(); setUserDDOpen(false); }} style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      background: 'none', border: 'none',
                      color: 'rgba(255,100,100,0.8)', padding: '10px 14px', cursor: 'pointer',
                      fontFamily: F, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textAlign: 'left',
                      transition: 'background .12s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(240,60,60,0.07)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      🚪 {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  title={t('nav.login')}
                  onClick={() => { setAuthMode('login'); setShowAuth(true); }}
                  className="topbar-auth-btn"
                >
                  <span>👤</span>
                  <span className="topbar-auth-label">{t('nav.login')}</span>
                </button>
                <button
                  title={t('nav.register')}
                  onClick={() => navigate('/register')}
                  className="topbar-auth-btn topbar-auth-btn-reg"
                >
                  <span>📝</span>
                  <span className="topbar-auth-label">{t('nav.register')}</span>
                </button>
              </div>
            )}
          </div>

          {/* ── Hamburger — mobile only ── */}
          <div className="topbar-hamburger" style={{ display: 'none', alignItems: 'center', gap: 7, marginLeft: 'auto' }}>
            <button onClick={() => setDark(d => !d)} style={{ background: 'none', border: 'none', fontSize: 15, cursor: 'pointer', padding: 4 }}>
              {dark ? '☀️' : '🌙'}
            </button>
            <button
              onClick={() => setMenuOpen(o => !o)}
              style={{
                background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 8, width: 38, height: 38,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5,
                cursor: 'pointer',
              }}
            >
              {[0,1,2].map(i => (
                <span key={i} style={{
                  display: i === 2 && menuOpen ? 'none' : 'block',
                  width: 16, height: 2, background: '#fff', borderRadius: 2, transition: '.2s',
                  transform: menuOpen && i === 0 ? 'rotate(45deg) translate(5px,5px)' : menuOpen && i === 1 ? 'rotate(-45deg)' : 'none',
                }} />
              ))}
            </button>
          </div>
        </div>

        {/* ── Bottom ornament ── */}
        <div style={{ height: 1, background: 'linear-gradient(90deg,transparent,rgba(240,192,64,0.28),rgba(123,79,212,0.18),rgba(240,192,64,0.28),transparent)' }} />
      </header>

      {/* ── Mobile drawer ── */}
      {menuOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.58)' }}
          onClick={() => setMenuOpen(false)}
        >
          <div
            style={{
              position: 'absolute', top: 0, right: 0, bottom: 0, width: 282,
              background: 'linear-gradient(180deg,#09061a,#060412)',
              borderLeft: '1px solid rgba(240,192,64,0.16)',
              display: 'flex', flexDirection: 'column',
              boxShadow: '-4px 0 48px rgba(0,0,0,0.72)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(240,192,64,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: F, fontSize: 13, color: '#f0c040', letterSpacing: 2.5, textShadow: '0 0 12px rgba(240,192,64,0.4)' }}>
                ⚔️ FANTASTAI.LT
              </span>
              <button onClick={() => setMenuOpen(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
              {[...navLinks, { path: '/news', label: t('nav.news') }, { path: '/publishers', label: t('nav.publishers') }, { path: '/players', label: t('nav.players') }].map(({ path, label }) => {
                const act = isActive(path);
                return (
                  <button key={path} onClick={() => goTo(path)} style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    background: act ? 'rgba(240,192,64,0.06)' : 'none',
                    border: 'none', borderLeft: `3px solid ${act ? '#f0c040' : 'transparent'}`,
                    color: act ? '#f0c040' : 'rgba(210,195,250,0.78)',
                    padding: '12px 20px', cursor: 'pointer',
                    fontFamily: F, fontSize: 12, fontWeight: 700, letterSpacing: 1.5,
                    textTransform: 'uppercase', transition: 'all .15s',
                  }}>
                    {label}
                  </button>
                );
              })}

              <div style={{ padding: '10px 20px 8px', borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: 6 }}>
                <div style={{ fontSize: 9, color: 'rgba(240,192,64,0.4)', letterSpacing: 2, fontFamily: F, marginBottom: 8, textTransform: 'uppercase' }}>{t('events_menu.section')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {EVENTS_SUBMENU.map(item => (
                    <button key={item.path} onClick={() => goTo(item.path)} style={{
                      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 6, color: 'rgba(210,195,250,0.7)',
                      padding: '5px 10px', cursor: 'pointer',
                      fontFamily: F, fontSize: 10, fontWeight: 700, letterSpacing: 1,
                    }}>
                      {item.icon} {t(`events_menu.${item.key}_title`).toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ padding: '10px 20px 8px', borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: 6 }}>
                <div style={{ fontSize: 9, color: 'rgba(240,192,64,0.4)', letterSpacing: 2, fontFamily: F, marginBottom: 8, textTransform: 'uppercase' }}>{t('news.categories')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {NEWS_CATEGORIES.map(cat => (
                    <button key={cat.id} onClick={() => goTo(`/news?cat=${cat.id}`)} style={{
                      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 6, color: 'rgba(210,195,250,0.7)',
                      padding: '5px 10px', cursor: 'pointer',
                      fontFamily: F, fontSize: 10, fontWeight: 700, letterSpacing: 1,
                    }}>
                      {cat.icon} {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {isAdmin && (
                <button
                  onClick={() => { goTo('/admin'); setMenuOpen(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    background: 'none', border: 'none', borderLeft: '3px solid transparent',
                    color: '#ffa040', padding: '12px 20px', cursor: 'pointer',
                    fontFamily: F, fontSize: 12, fontWeight: 700, letterSpacing: 1.5,
                    textTransform: 'uppercase', marginTop: 4,
                  }}
                >
                  ⚙️ {t('nav.admin')}
                </button>
              )}
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(240,192,64,0.1)' }}>
              <div style={{ fontSize: 9, color: 'rgba(210,195,250,0.38)', letterSpacing: 2, fontFamily: F, textTransform: 'uppercase', marginBottom: 8 }}>{t('common.language')}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {LANGS.map(l => (
                  <button key={l} onClick={() => changeLang(l)} style={{
                    background: i18n.language === l ? 'rgba(240,192,64,0.16)' : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${i18n.language === l ? 'rgba(240,192,64,0.5)' : 'rgba(255,255,255,0.12)'}`,
                    borderRadius: 5, color: i18n.language === l ? '#f0c040' : 'rgba(255,255,255,0.6)',
                    padding: '4px 7px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: F,
                  }}>
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(240,192,64,0.1)' }}>
              {user ? (
                <>
                  <div style={{ color: '#f0c040', fontSize: 12, fontFamily: F, letterSpacing: 1, marginBottom: 10 }}>👤 {user.name}</div>
                  <button onClick={() => { logout(); setMenuOpen(false); }} style={{
                    width: '100%', background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
                    color: 'rgba(210,195,250,0.8)', padding: '8px',
                    cursor: 'pointer', fontFamily: F, fontSize: 10, letterSpacing: 1,
                  }}>
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setAuthMode('login'); setShowAuth(true); setMenuOpen(false); }} style={{
                    flex: 1, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: 6, color: 'rgba(210,195,250,0.85)', padding: '9px 6px',
                    cursor: 'pointer', fontFamily: F, fontSize: 11, fontWeight: 700, letterSpacing: 1,
                  }}>👤 {t('nav.login')}</button>
                  <button onClick={() => { navigate('/register'); setMenuOpen(false); }} style={{
                    flex: 1,
                    background: 'linear-gradient(135deg,rgba(240,192,64,0.12),rgba(123,79,212,0.12))',
                    border: '1px solid rgba(240,192,64,0.35)', borderRadius: 6,
                    color: '#f0c040', padding: '9px 6px',
                    cursor: 'pointer', fontFamily: F, fontSize: 11, fontWeight: 700, letterSpacing: 1,
                  }}>📝 {t('nav.register')}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} initialTab={authMode} />}
    </>
  );
}
